<template>
  <BlBackdrop :footer="false">
    <div class="flex flex-col gap-2 px-8 pt-8 pb-4">
      <BlTextTitle4
        v-if="destinationStage.is_admitted"
        text="Confirm client admission"
      />
      <BlTextTitle4
        v-if="destinationStage.is_discontinued"
        text="Reason for discontinuation"
      />
      <div class="flex">
        <div class="flex items-center gap-1 px-1 rounded h-7 bg-tulip-100">
          <BlIcon icon="user-square" color="tulip-7" class="w-6 h-6" />
          <BlTextSubtitle3
            :text="prospectStage.prospect.name"
            class="text-tulip-700"
          />
        </div>
      </div>
    </div>
    <div
      v-if="celebrationPopup"
      class="flex flex-col items-center justify-center w-full h-full px-8 pb-8"
    >
      <BlGif gif="celebration" class="rounded-full h-72 w-72" />
      <BlTextTitle4
        class="text-light-purple"
        text="Congratulations! You've just converted a"
      />
      <BlTextTitle4 class="text-light-purple" text="prospect into a client! " />
    </div>
    <div
      v-else-if="modalLoading"
      class="flex flex-col items-center justify-center gap-4 px-8 overflow-y-auto h-128"
      :style="{ width: '40rem' }"
    >
      <div class="blum-loader"></div>
    </div>
    <div v-else :style="{ width: '40rem' }">
      <div
        v-if="destinationStage.is_discontinued"
        class="flex flex-col gap-4 px-8"
      >
        <BlTextBody2
          text="Please provide the reason for the prospect's decision to discontinue during the client acquisition process. If the prospect returns, their profile can be reopened at any time."
        />
        <div class="flex flex-col gap-1">
          <BlSelectBox
            v-model="reasonInput"
            placeholder="Select reason"
            :options="reasonOptions"
            @change="reasonInput = $event"
          />
          <BlInputText
            v-if="reasonInput === 'other'"
            :input="otherReasonInput"
            @changed="inputHandler"
          />
        </div>
      </div>
      <div
        v-if="destinationStage.is_admitted"
        class="flex flex-col gap-4 px-8 overflow-y-auto"
        :style="{ maxHeight: 'calc(100vh - 8rem)' }"
      >
        <div>
          <BlTextBody2 text="By moving this prospect to " />
          <BlTextBody2 text="''Admitted'' " class="font-bold" />
          <BlTextBody2
            text=", you'll create a client profile using their acquisition information. Before finalizing, you can select the necessary files/ documents to include in the client profile. These documents will be accessible to everyone in the center."
          />
        </div>
        <div
          v-if="prospectInformation"
          class="flex flex-col gap-2 p-2 rounded bg-slate-200"
        >
          <BlTextBody2
            v-if="!prospectInformation.documents.length"
            text="No documents available to include in the client profile."
            class="text-center"
          />
          <BlFileHolder
            v-else
            v-for="doc in prospectInformation.documents"
            :key="doc.id"
            :name="doc.blob.filename"
            :size="doc.blob.byte_size"
            checkable
            :checked="documentsInput.includes(doc.id)"
            @check-handler="onCheckDoc(doc.id)"
          />
        </div>
        <div v-if="enableBranch" class="flex flex-col gap-2">
          <BlTextBody2
            text="Select branch tag for this prospect. The tag will be visible in their profile. (Optional)"
          />
          <BlSearchBox
            type="branch"
            drop="up"
            placeholder="Type tag"
            v-model="branchesInput"
            :options="branchOptions"
            @change="branchesInput = $event"
          />
        </div>
        <div>
          <BlTextBody2
            text="You can always edit the information in the client profile later if needed."
          />
        </div>
      </div>
    </div>
    <div
      v-if="!celebrationPopup"
      class="flex items-center justify-end gap-4 px-8 pt-4 pb-8"
    >
      <BlInputButton
        type="plain"
        label="Cancel"
        @clicked="$emit('close-modal')"
      />
      <BlInputButton
        :label="destinationStage.is_admitted ? 'Confirm admission' : 'Submit'"
        :disabled="isDisabledSubmit"
        :loading="submitLoading"
        @clicked="onSubmit"
      />
    </div>
  </BlBackdrop>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import BlBackdrop from "../../BlBackdrop.vue";
import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlGif from "../../BlGif.vue";
import BlInputText from "../../BlInputText.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlFileHolder from "../../BlFileHolder.vue";
import BlSearchBox from "../../BlSearchBox.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "MoveProspectModal",
  components: {
    BlBackdrop,
    BlTextTitle4,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlGif,
    BlInputText,
    BlSelectBox,
    BlFileHolder,
    BlSearchBox,
    BlInputButton
  },
  props: {
    location: { type: String, default: "table" },
    prospectStage: { type: Object, required: true },
    destinationStage: { type: Object, required: true }
  },
  data() {
    return {
      celebrationPopup: false,
      modalLoading: false,
      submitLoading: false,
      reasonInput: "",
      reasonOptions: [
        { value: "fees_too_high", label: "Fees too high" },
        { value: "distance_too_far", label: "Distance too far" },
        {
          value: "unable_to_contact_family",
          label: "Unable to contact family"
        },
        { value: "hours_dont_fit_schedule", label: "Hours don't fit schedule" },
        {
          value: "seeking_service_not_provided",
          label: "Seeking services not provided"
        },
        { value: "too_old", label: "Too old" },
        {
          value: "resides_in_a_different_state",
          label: "Resides in a different state"
        },
        { value: "no_show_response", label: "No show/response" },
        { value: "do_not_need_therapy", label: "Do not need therapy" },
        { value: "ongoing_client", label: "Ongoing client" },
        { value: "other", label: "Other" }
      ],
      otherReasonInput: {
        textarea: true,
        placeholder: "Type the reason",
        model: ""
      },
      documentsInput: [],
      branchesInput: []
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "branches",
      "prospectInformation"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    branchOptions() {
      return this.branches.map(i => ({
        value: i.id,
        label: i.name,
        color: i.color,
        backgroundColor: i.background_color
      }));
    },
    isDisabledSubmit() {
      if (this.destinationStage.is_discontinued) {
        if (!this.reasonInput) return true;
        if (this.reasonInput === "other") {
          if (!this.otherReasonInput.model) return true;
        }
      }
      return false;
    }
  },
  created() {},
  async mounted() {
    this.modalLoading = true;
    const id = this.prospectStage.prospect.id;
    this.getProspect({ id }).then(({ data }) => {
      this.modalLoading = false;
      if (data.branch_id) {
        this.branchesInput = [data.branch_id];
      }
    });
  },
  methods: {
    ...mapActions([
      "getProspect",
      "moveProspect",
      "getStageProspectStages",
      "getProspectStages"
    ]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    onCheckDoc(docId) {
      if (this.documentsInput.includes(docId)) {
        this.documentsInput = this.documentsInput.filter(i => i !== docId);
      } else {
        this.documentsInput = [...this.documentsInput, docId];
      }
    },
    onSubmit() {
      const data = {
        id: this.prospectInformation.id,
        stage_id: this.destinationStage.id,
        prospect_name: this.prospectInformation.name,
        stage_name: this.destinationStage.name
      };
      if (this.destinationStage.is_discontinued) {
        data["discontinuation_reason"] = this.reasonInput;
        if (this.reasonInput === "other") {
          data["other_discontinuation_reason"] = this.otherReasonInput.model;
        }
      }
      if (this.destinationStage.is_admitted) {
        data["document_ids"] = this.documentsInput;
        data["branch_ids"] = this.branchesInput;
      }
      this.submitLoading = true;
      this.moveProspect(data).then(({ success }) => {
        if (!success) {
          this.submitLoading = false;
          return;
        }
        let timeout = 10;
        if (this.destinationStage.is_admitted) {
          this.celebrationPopup = true;
          timeout = 2000;
        }
        setTimeout(async () => {
          if (this.location === "table") {
            this.getProspectStages();
          }
          if (this.location === "kanban") {
            this.getStageProspectStages({ id: this.prospectStage.stage.id });
            this.getStageProspectStages({ id: this.destinationStage.id });
          }
          this.$emit("close-modal");
          this.submitLoading = false;
        }, timeout);
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/salesPipeline/modal/MoveProspectModal.vue